import React, { useState } from "react";
import Slider from "react-slick";
import tw from "twin.macro";
import styled from "styled-components";
import { SectionHeading } from "components/misc/Headings";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons";
import { ReactComponent as DeleteIcon } from "feather-icons/dist/icons/trash-2.svg";
import { ReactComponent as LocationIcon } from "feather-icons/dist/icons/map-pin.svg";
import { ReactComponent as StarIcon } from "feather-icons/dist/icons/star.svg";
import { ReactComponent as WarningIcon } from "feather-icons/dist/icons/alert-triangle.svg";
import { ReactComponent as ChevronLeftIcon } from "feather-icons/dist/icons/chevron-left.svg";
import { ReactComponent as ChevronRightIcon } from "feather-icons/dist/icons/chevron-right.svg";
import { ReactComponent as PhoneIcon } from "feather-icons/dist/icons/phone-call.svg";
import { ReactComponent as ShareIcon } from "feather-icons/dist/icons/share-2.svg";
import { ReactComponent as CopyIcon } from "feather-icons/dist/icons/copy.svg";
import { VisitorApi } from "utils/api";
import { NotificationContainer, NotificationManager } from "react-notifications";
import PropTypes from "prop-types";

const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-16 lg:py-20`;

const HeadingWithControl = tw.div`flex flex-col items-center sm:items-stretch sm:flex-row justify-between`;
const Heading = tw(SectionHeading)``;
const Controls = tw.div`flex items-center`;
const ControlButton = styled(PrimaryButtonBase)`
  ${tw`mt-4 sm:mt-0 first:ml-0 ml-6 rounded-full p-2`}
  svg {
    ${tw`w-6 h-6`}
  }
`;
const PrevButton = tw(ControlButton)``;
const NextButton = tw(ControlButton)``;

const CardSlider = styled(Slider)`
  ${tw`mt-16`}
  .slick-track { 
    ${tw`flex`}
  }
  .slick-slide {
    ${tw`h-auto flex justify-center mb-1 w-full`}
    > div {
      ${tw`w-full flex justify-center h-full`}
    }
  }
`;

const CardCenter = styled.div`
${tw`h-auto flex flex-col items-center md:items-stretch md:flex-row flex-wrap md:justify-between max-w-screen-xl mx-auto py-20 md:py-24`}
`;

const Card = styled.div`
  ${tw`h-full flex! flex-col max-w-xs relative focus:outline-none`}
  background: linear-gradient(90deg, #e0efff 0%, #fff 100%);
  border-radius: 24px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.07);
  margin: 1rem;
  border: none;
`;

const CardAlone = styled.div`
  ${tw`h-full flex! flex-col max-w-xs relative focus:outline-none`}
  background: linear-gradient(90deg, #e0efff 0%, #fff 100%);
  border-radius: 24px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.07);
  margin: 1rem;
  border: none;
`;


//const TextInfo = tw.div`py-6 sm:px-10 sm:py-6`;
const TextInfo = tw.div`py-6 px-6 sm:px-10 sm:py-6 break-words`;
const TitleReviewContainer = tw.div`flex flex-col sm:flex-row sm:justify-between sm:items-center`;
const Title = tw.h5`text-2xl font-bold`;

const RatingsInfo = styled.div`
  ${tw`flex items-center sm:ml-4 mt-2 sm:mt-0`}
  svg {
    ${tw`w-6 h-6 text-yellow-500 fill-current`}
  }
`;
const RatingsInfoNoFill = styled.div`
  ${tw`flex items-center sm:ml-4 mt-2 sm:mt-0`}
  svg {
    ${tw`w-6 h-6 text-yellow-500`}
  }
`;
const Rating = tw.span`ml-2 font-bold`;
const Description = tw.p`text-sm leading-loose mt-2 sm:mt-4`;

const SecondaryInfoContainer = tw.div`flex flex-col sm:flex-row mt-2 sm:mt-4`;
const IconWithText = tw.div`flex items-center mr-6 my-2 sm:my-0`;
const IconContainer = styled.div`
  ${tw`inline-block rounded-full p-2 bg-gray-700 text-gray-100`}
  svg {
    ${tw`w-3 h-3`}
  }
`;
const Text = tw.div`ml-2 text-sm font-semibold text-gray-800`;
//const WhatsAppText = tw.div`ml-2 text-lg font-bold text-green-600`;
const Text2 = tw.div`ml-2 font-bold text-sm text-red-800`;
const IconContainer2 = styled.div`
  ${tw`inline-block cursor-pointer rounded-full p-2 bg-red-700 text-gray-100`}
  svg {
    ${tw`w-3 h-3`}
  }
`;

// wa.me deep-link sharing button (no WhatsApp API — homeowner's personal WhatsApp)
const WaShareButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #25D366;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s;
  &:hover { background: #1ebe5d; color: #fff; }
`;

const CopyLinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #edf2f7;
  color: #2d3748;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #e2e8f0; }
`;

const PrimaryButton = styled(PrimaryButtonBase)`
  ${tw`mt-auto text-xl w-full py-6 text-center font-bold`}
  background: linear-gradient(90deg, #4299e180 0%, #072448ff 100%);
  color: #fff;
  font-weight: 700;
  border: none;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 2px 8px rgba(99,102,241,0.15);
  cursor: pointer;
  letter-spacing: 0.5px;
  transition: background 0.2s, transform 0.2s;
  &:hover {
    background: linear-gradient(90deg, #4299e180 0%, #0e6cdeff 100%);
    transform: translateY(-2px);
  }
`;

const VisitorCards = ({ title, visitorCards, Icon, getVisitors }) => {
  const [sliderRef, setSliderRef] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const copyLink = (id, url) => {
    navigator.clipboard?.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      NotificationManager.warning("No se pudo copiar al portapapeles", "Error", 3000);
    });
  };

  const sliderSettings = {
    arrows: false,
    slidesToShow: 3,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 2,
        }
      },

      {
        breakpoint: 900,
        settings: {
          slidesToShow: 1,
        }
      },
    ]
  };

  const cancelInvitation = async (e, id) => {
    e.preventDefault();
    await VisitorApi.deleteVisitor(id).then((response) => {
      if (response.status == 200) {
        createNotification('success', 'Invitación eliminada con exito.', 'Operación exitosa!');
        getVisitors();
      }
      else {
        createNotification('info', response.data.statusCode, response.data.errorMsg);
      }
    }).catch((error) => {
      createNotification('error', error.message, error.name);
    });
  };

  const createNotification = (type, message, title) => {
    switch (type) {
      case 'info':
        NotificationManager.info(message, title, 5000);
        break;
      case 'success':
        NotificationManager.success(message, title, 5000);
        break;
      case 'warning':
        NotificationManager.warning(message, title, 5000);
        break;
      case 'error':
        NotificationManager.error(message, title, 5000);
        break;
      default:
        break;
    }
  };

  return (
    <>{visitorCards.length > 2 &&
      <Container>
        <Content>
        <div style={{ width: '100%', maxWidth: '1280px', margin: '0 auto', padding: '0 1rem' }}>
          <HeadingWithControl>
            <Heading>{title}</Heading>
            <Controls>
              <PrevButton onClick={sliderRef?.slickPrev}><ChevronLeftIcon /></PrevButton>
              <NextButton onClick={sliderRef?.slickNext}><ChevronRightIcon /></NextButton>
            </Controls>
          </HeadingWithControl>
        </div>
          <CardSlider ref={setSliderRef} {...sliderSettings}>
            {visitorCards.map((card) => (
              <Card key={card.id}>
                {/* <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <QRCode value={card.id} bgColor="white" fgColor="black" sise={200} />
                </div> */}
                <TextInfo>
                  <TitleReviewContainer>
                    <Title>{card.title}</Title>
                    {Icon === 'StarIcon' &&
                      <RatingsInfo>
                        <StarIcon />
                        <Rating>{card.rating}</Rating>
                      </RatingsInfo>}
                    {Icon === 'WarningIcon' &&
                      <RatingsInfoNoFill>
                        <WarningIcon />
                        <Rating>{card.rating}</Rating>
                      </RatingsInfoNoFill>}
                  </TitleReviewContainer>
                  <SecondaryInfoContainer>
                    <IconWithText>
                      <IconContainer>
                        <LocationIcon />
                      </IconContainer>
                      <Text>{card.locationText}</Text>
                    </IconWithText>
                    <IconWithText>
                      <IconContainer2 onClick={(e) => cancelInvitation(e, card.id)}>
                        <DeleteIcon />
                      </IconContainer2>
                      <Text2>{card.deleteText}</Text2>
                    </IconWithText>
                  </SecondaryInfoContainer>
                  <SecondaryInfoContainer>
                    <IconWithText>
                      <IconContainer>
                        <PhoneIcon />
                      </IconContainer>
                      <Text>{card.phone}</Text>
                    </IconWithText>
                  </SecondaryInfoContainer>
                  <Description>{card.description}</Description>
                  <SecondaryInfoContainer style={{ gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                    <WaShareButton
                      href={card.waLink || `https://wa.me/${card.phone}?text=${encodeURIComponent(card.message)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ShareIcon style={{ width: 14, height: 14 }} />
                      Compartir vía WhatsApp
                    </WaShareButton>
                    <CopyLinkButton onClick={() => copyLink(card.id, card.url)}>
                      <CopyIcon style={{ width: 14, height: 14 }} />
                      {copiedId === card.id ? '¡Copiado!' : 'Copiar link'}
                    </CopyLinkButton>
                  </SecondaryInfoContainer>
                </TextInfo>
                <PrimaryButton as="a" href={card.url} target="_blank" rel="noopener noreferrer">
                  Ver Link de Invitación
                </PrimaryButton>
              </Card>
            ))}
          </CardSlider>
        </Content>
      </Container>
    }

      {(visitorCards.length <= 2 && visitorCards.length > 0) &&
        <Container>
          <Content>
            <HeadingWithControl>
              <Heading>{title}</Heading>
            </HeadingWithControl>
            <CardCenter>
              {visitorCards.map((card) => (
                <CardAlone key={card.id}>
                  {/* <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <QRCode value={card.id} bgColor="white" fgColor="black" sise={200} />
                  </div> */}

                  <TextInfo>
                    <TitleReviewContainer>
                      <Title>{card.title}</Title>
                      {Icon === 'StarIcon' &&
                        <RatingsInfo>
                          <StarIcon />
                          <Rating>{card.rating}</Rating>
                        </RatingsInfo>}
                      {Icon === 'WarningIcon' &&
                        <RatingsInfoNoFill>
                          <WarningIcon />
                          <Rating>{card.rating}</Rating>
                        </RatingsInfoNoFill>}
                    </TitleReviewContainer>
                    <SecondaryInfoContainer>
                      <IconWithText>
                        <IconContainer>
                          <LocationIcon />
                        </IconContainer>
                        <Text>{card.locationText}</Text>
                      </IconWithText>
                      <IconWithText>
                        <IconContainer2 onClick={(e) => cancelInvitation(e, card.id)}>
                          <DeleteIcon />
                        </IconContainer2>
                        <Text2>{card.deleteText}</Text2>
                      </IconWithText>
                    </SecondaryInfoContainer>
                    <SecondaryInfoContainer>
                      <IconWithText>
                        <IconContainer>
                          <PhoneIcon />
                        </IconContainer>
                        <Text>{card.phone}</Text>
                      </IconWithText>
                    </SecondaryInfoContainer>
                    <Description>{card.description}</Description>
                    <SecondaryInfoContainer style={{ gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                      <WaShareButton
                        href={card.waLink || `https://wa.me/${card.phone}?text=${encodeURIComponent(card.message)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ShareIcon style={{ width: 14, height: 14 }} />
                        Compartir vía WhatsApp
                      </WaShareButton>
                      <CopyLinkButton onClick={() => copyLink(card.id, card.url)}>
                        <CopyIcon style={{ width: 14, height: 14 }} />
                        {copiedId === card.id ? '¡Copiado!' : 'Copiar link'}
                      </CopyLinkButton>
                    </SecondaryInfoContainer>
                  </TextInfo>
                  <PrimaryButton as="a" href={card.url} target="_blank" rel="noopener noreferrer">
                    Ver Link de Invitación
                  </PrimaryButton>
                  <br></br>
                </CardAlone>
              ))}
            </CardCenter>
          </Content>

          <NotificationContainer />
        </Container>
      }
    </>
  );
};

VisitorCards.propTypes = {
  title: PropTypes.string,
  visitorCards: PropTypes.array.isRequired,
  Icon: PropTypes.string,
  getVisitors: PropTypes.func
};

export default VisitorCards;
